import React, { useEffect } from "react";
import Hero from "@/components/Hero";
import EditorialAccordion from "@/components/EditorialAccordion";
import VignetteCard from "@/components/VignetteCard";
import OffersSection from "@/components/OffersSection";
import Footer from "@/components/Footer";
import { Send, Youtube, ArrowUpRight, Instagram } from "lucide-react";
import portrait2Img from "@/assets/da/portrait-2.inline";

// Real branded AI artworks pulled directly from gillesdelieuze.com — vibrant, on-brand.
const G = "https://gillesdelieuze.com/wp-content/uploads";
const IMG = {
    // Section 1 — Quizz (vibrant energy auras)
    dauphinTurquoise: `${G}/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_turquois_8cbec288-5125-4e13-8bab-c1d122dcb180.png`,
    dauphinRose: `${G}/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_rose_dau_74da70fd-fc44-4eb5-bc3a-2248cc164c13.png`,
    dauphinViolet: `${G}/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_violet_d_1528da77-503d-4b09-932a-c40dc1dccfaf.png`,
    dauphinDiamant: `${G}/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_diamond__568ffd55-e58d-4455-b34f-975c4493bf84.png`,
    dauphinRainbow: `${G}/2024/11/gilles1385_photo_futuriste_figuratif_Dolphin_Celestial_rainbow__7d49a903-744c-4b40-b335-7a3073a0f6fa.png`,
    dauphinNeon: `${G}/2025/03/gilles1385_dolphins_joy_happiness_neon_stellar_furist_ultra_det_d108e6d0-77b9-454e-9100-27fcbd17df2c-650x650.png`,
    cerveauSynapses: `${G}/2024/03/gilles1385_full_brain_cerveau_synapses_neurones_lumiere_eveil_f_463c094e-7c71-416e-967f-23e686570fe1.png`,
    merkaba: `${G}/2023/10/gilles1385_merkaba_geometry_sacree_3d_gold_diamond_Bright_color_89af9181-4a2e-406e-92c1-741617b54425.png`,
    fractalDNA: `${G}/2023/10/gilles1385_fractal_dna_helix_cyberpunk_galactic_quantum_8k_divi_df62ecc0-0fb7-42c4-85cf-1df4d07bc1aa.png`,
    geometrieAmen: `${G}/2023/10/gilles1385_symbole_3d_geometry_sacree_pour_decrire_le_mot_AMEN__038ebd6a-7b68-4887-8fda-a3ecae2d5c8d.png`,
    chakras: `${G}/2025/02/gilles1385_figurative_futurist_photo_human_body_with_chakras_li_f3b9c26d-4156-4461-aafe-d50d95afe9b1.png`,
    bestLife: `${G}/2023/10/gilles1385_photo_Best_Life_illumination_Life_grace_ascension_ma_71b41840-fd97-49be-b309-6faf5d529a9b.png`,
    meditationCristal: `${G}/2024/08/gilles1385_12_humains_qui_medites_avec_des_dauphins_de_cristal__21acedb8-2c3b-425f-80ee-927aebe800ab-650x650.png`,
    // Voyages Mer Rouge — real underwater photos
    dauphinUnderwater: "https://images.pexels.com/photos/15210605/pexels-photo-15210605.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600",
    dauphinSataya: "https://images.pexels.com/photos/4321166/pexels-photo-4321166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600",
    dauphinsPod: "https://images.pexels.com/photos/5008831/pexels-photo-5008831.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1200",
    // Portraits Gilles
    gillesGuide: `${G}/2024/03/IMG_0258_www.vincentphotographie.com_-1024x683.jpg`,
    gillesAxiom: `${G}/2024/03/IMG_9977_www.vincentphotographie.com_-1024x683.jpg`,
    gillesAccordeur: `${G}/2022/11/1-2-724x1024.jpg`,
    gilles11: `${G}/2024/03/IMG_0013_www.vincentphotographie.com_-650x650.jpg`,
    gillesJoie: `${G}/2024/03/IMG_9881_www.vincentphotographie.com_-683x1024.jpg`,
    portrait2: portrait2Img,
};

const SectionEyebrow = ({ index, label, accent }) => (
    <div className="flex items-center gap-3 mb-5">
        <span className="text-[11px] font-mono-tracked text-champagne/90">
            {String(index).padStart(2, "0")} · {label}
        </span>
        {accent && (
            <span className="text-[10px] font-mono-tracked text-champagne bg-champagne/15 border border-champagne/50 rounded-full px-3 py-0.5 shadow-[0_0_18px_-4px_rgba(212,175,55,0.4)]">
                {accent}
            </span>
        )}
        <span className="h-px flex-1 bg-gradient-to-r from-champagne/45 via-champagne/15 to-transparent" />
    </div>
);

const Portal = () => {
    useEffect(() => {
        document.title = "Portail · Gilles Delieuze";
    }, []);

    return (
        <main className="bg-organic min-h-screen text-ivory overflow-x-hidden">
            <Hero />

            <section
                id="sections"
                data-testid="sections-wrap"
                className="relative pt-16 sm:pt-20 md:pt-28 pb-20 md:pb-32 grain bg-organic overflow-hidden"
            >
                <div className="halo bg-champagne/12 w-[420px] h-[420px] top-40 right-[-100px]" />
                <div className="halo bg-bronze/15 w-[360px] h-[360px] bottom-32 left-[-100px]" />

                <div className="relative max-w-4xl mx-auto px-4 sm:px-5 md:px-8 space-y-8 md:space-y-10">

                    {/* SECTION 1 — Ressources GRATUITES */}
                    <div>
                        <SectionEyebrow index={1} label="Ressources gratuites" accent="100 % OFFERT" />
                        <EditorialAccordion
                            index={1}
                            eyebrow="Portail offert"
                            title="Quizz, fréquences & protocoles"
                            subtitle="Trois quizz initiatiques + canal Telegram. Entièrement offerts."
                            thumbnail={IMG.dauphinRainbow}
                            testid="section-ressources"
                        >
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                                <VignetteCard
                                    eyebrow="Quizz"
                                    title="Dauphin allié"
                                    body="Découvrir le dauphin qui te guide"
                                    badge="Offert"
                                    href="https://gillesdelieuze.com/"
                                    image={IMG.dauphinTurquoise}
                                    testid="vignette-quiz-dauphin"
                                    aspect="square"
                                />
                                <VignetteCard
                                    eyebrow="Quizz"
                                    title="Pattern"
                                    body="Reconnaître tes patterns inconscients"
                                    badge="Offert"
                                    href="https://gillesdelieuze.com/quizz-pattern/"
                                    image={IMG.cerveauSynapses}
                                    testid="vignette-quiz-pattern"
                                    aspect="square"
                                />
                                <VignetteCard
                                    eyebrow="Quizz"
                                    title="Fréquences"
                                    body="Ta fréquence sacrée du moment"
                                    badge="Offert"
                                    href="https://gillesdelieuze.com/quizz-frequences/"
                                    image={IMG.merkaba}
                                    testid="vignette-quiz-frequences"
                                    aspect="square"
                                />
                                <VignetteCard
                                    eyebrow="Canal"
                                    title="Telegram"
                                    body="Protocoles hebdo offerts"
                                    badge="Live"
                                    href="https://t.me/+zLObzvUAXjVlYmY0"
                                    image={IMG.fractalDNA}
                                    testid="vignette-telegram"
                                    aspect="square"
                                />
                            </div>
                        </EditorialAccordion>
                    </div>

                    {/* SECTION 2 — Formations */}
                    <div>
                        <SectionEyebrow index={2} label="Formations & accompagnements" />
                        <EditorialAccordion
                            index={2}
                            eyebrow="Voies initiatiques"
                            title="Formations & accompagnements"
                            subtitle="École, cursus, mentorat, 1:1, programmes — à votre rythme."
                            thumbnail={IMG.dauphinDiamant}
                            testid="section-formations"
                        >
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                                <VignetteCard
                                    eyebrow="01 · Voie phare"
                                    title="École des Maîtres Dauphins"
                                    body="La voie initiatique principale"
                                    badge="Phare"
                                    href="https://gillesdelieuze.com/lecole-des-maitres-dauphins/"
                                    image={IMG.dauphinNeon}
                                    testid="vignette-ecole"
                                    aspect="portrait"
                                />
                                <VignetteCard
                                    eyebrow="02"
                                    title="Accordeur Quantique"
                                    body="Réharmonisation profonde"
                                    href="https://gillesdelieuze.com/accordeur-quantique-2024/"
                                    image={IMG.fractalDNA}
                                    testid="vignette-accordeur"
                                    aspect="portrait"
                                />
                                <VignetteCard
                                    eyebrow="03 · Mentorat"
                                    title="AXIOM Cursus"
                                    body="Immersion pour leaders"
                                    href="https://gillesdelieuze.com/axiom-cursus-immersion/"
                                    image={IMG.gillesAxiom}
                                    testid="vignette-axiom"
                                    aspect="portrait"
                                />
                                <VignetteCard
                                    eyebrow="04 · Présence"
                                    title="Accompagnement 1:1"
                                    body="Sur-mesure et individuel"
                                    href="https://gillesdelieuze.com/accompagnements-11/"
                                    image={IMG.gilles11}
                                    testid="vignette-1-1"
                                    aspect="portrait"
                                />
                                <VignetteCard
                                    eyebrow="05 · Programme"
                                    title="Renaître à la Joie"
                                    body="Programme Joie Dauphin"
                                    href="https://gillesdelieuze.com/programme-joie-dauphin-2/"
                                    image={IMG.dauphinRose}
                                    testid="vignette-joie"
                                    aspect="portrait"
                                />
                            </div>
                        </EditorialAccordion>
                    </div>

                    {/* SECTION 3 — Voyages */}
                    <div>
                        <SectionEyebrow index={3} label="Voyages & immersions" />
                        <EditorialAccordion
                            index={3}
                            eyebrow="Immersions vivantes"
                            title="Mer Rouge — Immersion sacrée"
                            subtitle="Nager avec les dauphins sauvages. Édition limitée."
                            thumbnail={IMG.dauphinUnderwater}
                            testid="section-voyages"
                        >
                            <div className="grid md:grid-cols-2 gap-5 md:gap-7">
                                <VignetteCard
                                    eyebrow="Voyage · 2026"
                                    title="Mer Rouge"
                                    body="Nager avec les dauphins sauvages. Une immersion sacrée, places limitées."
                                    badge="Édition limitée"
                                    href="https://gillesdelieuze.com/voyages-gd/"
                                    image={IMG.dauphinSataya}
                                    testid="vignette-voyage-mer-rouge"
                                    aspect="video"
                                />
                                <div className="flex flex-col justify-center px-2 md:px-4">
                                    <p className="text-ivory/70 text-sm md:text-base leading-relaxed mb-6">
                                        Une parenthèse hors du temps, à la rencontre des dauphins
                                        sauvages, dans une lumière solaire qui traverse l'eau.
                                    </p>
                                    <a
                                        href="https://gillesdelieuze.com/voyages-gd/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="link-voyages"
                                        className="btn-pill btn-pill-primary self-start"
                                    >
                                        <span>Découvrir le voyage</span>
                                        <ArrowUpRight className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </EditorialAccordion>
                    </div>

                    {/* SECTION 4 — Me découvrir */}
                    <div>
                        <SectionEyebrow index={4} label="Me découvrir" />
                        <EditorialAccordion
                            index={4}
                            eyebrow="Documentaire & paroles"
                            title="Changer de regard"
                            subtitle="Interviews & conférences offertes. Une bibliothèque vivante."
                            thumbnail={IMG.gillesGuide}
                            thumbnailPosition="50% 28%"
                            testid="section-decouvrir"
                        >
                            <div className="grid md:grid-cols-12 gap-6 md:gap-8 items-stretch">
                                <a
                                    href="https://gillesdelieuze.com/a-propos/#videos"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="md:col-span-7 group relative block aspect-[4/5] md:aspect-[5/6] rounded-2xl overflow-hidden vignette-dark"
                                    data-testid="changer-regard-vignette"
                                >
                                    <img
                                        src={IMG.gillesGuide}
                                        alt="Gilles Delieuze — Interviews & conférences"
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1800ms] ease-out group-hover:scale-105"
                                        style={{ objectPosition: "50% 28%" }}
                                        loading="lazy"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/55 via-transparent to-transparent" />
                                </a>
                                <div className="md:col-span-5 flex flex-col justify-center">
                                    <p className="text-ivory/70 text-sm md:text-base leading-relaxed mb-6">
                                        Une parole incarnée, en présence. Toutes les interviews
                                        et conférences offertes au même endroit.
                                    </p>
                                    <a
                                        href="https://gillesdelieuze.com/a-propos/#videos"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="link-all-videos"
                                        className="btn-pill btn-pill-primary self-start"
                                    >
                                        <span>Toutes les interviews & conférences</span>
                                        <ArrowUpRight className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        </EditorialAccordion>
                    </div>

                    {/* SECTION 5 — Me rejoindre au quotidien */}
                    <div>
                        <SectionEyebrow index={5} label="Me rejoindre au quotidien" />
                        <EditorialAccordion
                            index={5}
                            eyebrow="Présence quotidienne"
                            title="Me rejoindre au quotidien"
                            subtitle="Canal vivant, chaîne, transmissions régulières."
                            thumbnail={IMG.portrait2}
                            thumbnailPosition="50% 20%"
                            testid="section-quotidien"
                        >
                            <div className="grid sm:grid-cols-3 gap-3 md:gap-4">
                                {[
                                    {
                                        label: "Telegram",
                                        hint: "Canal premium · présence vivante",
                                        href: "https://t.me/+zLObzvUAXjVlYmY0",
                                        icon: Send,
                                        testid: "daily-telegram",
                                    },
                                    {
                                        label: "YouTube",
                                        hint: "Chaîne officielle · transmissions",
                                        href: "https://www.youtube.com/@gillesdelieuze",
                                        icon: Youtube,
                                        testid: "daily-youtube",
                                    },
                                    {
                                        label: "Instagram",
                                        hint: "@gilles_delieuze · L'Agitateur",
                                        href: "https://www.instagram.com/gilles_delieuze/",
                                        icon: Instagram,
                                        testid: "daily-instagram",
                                    },
                                ].map(({ label, hint, href, icon: Icon, testid }) => (
                                    <a
                                        key={label}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group relative overflow-hidden rounded-2xl glass p-6 md:p-7 transition-all duration-500 hover:-translate-y-1"
                                        data-testid={testid}
                                    >
                                        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-champagne/15 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                                        <div className="relative flex items-start justify-between mb-8">
                                            <div className="w-11 h-11 rounded-full bg-champagne/10 flex items-center justify-center border border-champagne/30">
                                                <Icon className="w-5 h-5 text-champagne" />
                                            </div>
                                            <ArrowUpRight className="w-5 h-5 text-champagne/70 group-hover:rotate-12 group-hover:translate-x-0.5 transition-transform duration-500" />
                                        </div>
                                        <p className="relative font-display text-2xl md:text-3xl text-ivory leading-tight font-light mb-1">
                                            {label}
                                        </p>
                                        <p className="relative text-xs md:text-sm text-ivory/65">
                                            {hint}
                                        </p>
                                    </a>
                                ))}
                            </div>
                        </EditorialAccordion>
                    </div>
                </div>
            </section>

            <OffersSection />
            <Footer />
        </main>
    );
};

export default Portal;
